import React, {
  createContext,
  useContext,
  useState
} from "react";
import { OptionDefinition } from "@cloudscape-design/components/internal/components/option/interfaces";
import deploymentService from "../services/deploymentService";
import { DeploymentItem } from "types/deploymentTypes";




interface ClusterContextType {
  // Cluster list
  clusters: DeploymentItem[];
  loadingClusters: boolean;
  loadingClustersError: string | null;
  fetchClusters: () => Promise<void>;

  // Selected cluster
  selectedClusterDetails: DeploymentItem | null;
  selectedCluster: OptionDefinition | null;
  setSelectedCluster: (cluster: OptionDefinition | null) => void;
  restoreSelectedCluster: (clusterOptions: OptionDefinition[]) => void;
}


const ClusterContext = createContext<ClusterContextType | undefined>(undefined);


export const ClusterProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {

  const SELECTED_CLUSTER_STORAGE_KEY = "selectedClusterId";

  // -------------------------
  // Cluster list state
  // -------------------------

  const [clusters, setClusters] = useState<DeploymentItem[]>([]);
  const [loadingClusters, setLoadingClusters] = useState<boolean>(false);
  const [loadingClustersError, setLoadingClustersError] = useState<string | null>(null);


  // -------------------------
  // Selected cluster state
  // -------------------------

  const [selectedCluster, setSelectedClusterState] = useState<OptionDefinition | null>(null);

  // Get the full details of the currently selected cluster
  const selectedClusterDetails = clusters.find(
    cluster => cluster.id === selectedCluster?.value
  ) ?? null;


  const setSelectedCluster = (cluster: OptionDefinition | null) => {
    setSelectedClusterState(cluster);

    if (cluster) {
      localStorage.setItem(SELECTED_CLUSTER_STORAGE_KEY, cluster.value!);
    } else {
      localStorage.removeItem(SELECTED_CLUSTER_STORAGE_KEY);
    }
  };


  // Restores the previously selected cluster from localStorage if it still exists in the current cluster list.
  // Otherwise clears the stored selection.
  const restoreSelectedCluster = (
    clusterOptions: OptionDefinition[]
  ) => {
    const storedClusterId = localStorage.getItem(SELECTED_CLUSTER_STORAGE_KEY);

    if (!storedClusterId) {
      return;
    }

    const cluster = clusterOptions.find((option) => option.value === storedClusterId);

    if (cluster) {
      setSelectedClusterState(cluster);

    } else {
      localStorage.removeItem(SELECTED_CLUSTER_STORAGE_KEY);

      setSelectedClusterState(null);
    }
  };


  // -------------------------
  // Fetch clusters
  // -------------------------

  const fetchClusters = async () => {
    try {
      setLoadingClusters(true);
      setLoadingClustersError(null);

      const deployments = await deploymentService.fetchDedicatedClusters();

      setClusters(deployments);

      const clusterOptions: OptionDefinition[] = deployments.map(
        (cluster) => ({
          label: cluster.name,
          value: cluster.id,
        })
      );

      restoreSelectedCluster(clusterOptions);

    } catch (error) {
      setLoadingClustersError("Failed to load clusters. Please try again later.");

    } finally {
      setLoadingClusters(false);
    }
  };



  return (
    <ClusterContext.Provider
      value={{
        clusters,
        loadingClusters,
        loadingClustersError,
        fetchClusters,

        selectedClusterDetails,
        selectedCluster,
        setSelectedCluster,
        restoreSelectedCluster,
      }}
    >
      {children}
    </ClusterContext.Provider>
  );
};


export const useCluster = () => {
  const context = useContext(ClusterContext);

  if (!context) {
    throw new Error(
      "useCluster must be used inside ClusterProvider"
    );
  }

  return context;
};